/**
 * useSearchSuggestions Hook
 *
 * Diamond Architecture (Pure BFF): Uses server action for all API calls.
 * Browser never talks directly to Go backend.
 *
 * CONTROLLED: the query string is owned by the caller (so it can be lifted
 * above a teleporting container — see DockableHeroSearch — and survive a
 * remount). This hook is a pure function of `query` → suggestion sections:
 * - TanStack Query wrapping the BFF (race-safe, keepPreviousData)
 * - debounced query to avoid hammering the API during typing
 *
 * It also owns the COLLAPSE of its own observations into the closed
 * `SuggestionViewState` union (`@letsgethome/lib/search`). The data path is
 * the only place that knows the difference between "below the gate",
 * "debouncing", "in flight", "came back empty" and "failed", so it is the only
 * place allowed to decide which one the panel is in — the renderer just
 * switches, exhaustively (LET-78).
 */
import { bff } from '@/shared/lib/bff';
import { queryKeys } from '@/shared/lib/query-keys';
import type { SuggestionSectionData } from '@/shared/lib/types/suggestions';
import { SEARCH_DEBOUNCE_MS } from '@letsgethome/lib/constants';
import { deriveSuggestionViewState, type SuggestionViewState } from '@letsgethome/lib/search';
import { bffError } from '@letsgethome/lib/server-errors';
import { autocompleteQueryIsSearchable } from '@letsgethome/lib/wasm';
import { useDebouncedValue } from '@letsgethome/ui/hooks/use-debounced-value';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

interface UseSearchSuggestionsOptions {
  /** Debounce delay in milliseconds (default: the Rust search-SSOT debounce) */
  debounceMs?: number;
  /** Max results per section (default 5) */
  limit?: number;
}

interface UseSearchSuggestionsResult {
  /**
   * What the panel must render — the closed union, exhaustively. An empty
   * result set is `{ kind: 'empty' }` here, so it CANNOT be confused with
   * `{ kind: 'idle' }` the way `sections.length === 0` could.
   */
  state: SuggestionViewState<SuggestionSectionData[]>;
  /** Suggestion sections from server (empty below the minimum query length) */
  sections: SuggestionSectionData[];
  /** Loading state — drives the input's own spinner, which stays visible even
   *  while previous results are on screen. */
  isLoading: boolean;
  /** Error message, if any */
  error: string | null;
}

/** No section, or every section empty — the server answered with nothing. */
function noSuggestions(sections: SuggestionSectionData[]): boolean {
  return sections.every((s) => s.items.length === 0);
}

export function useSearchSuggestions(
  query: string,
  { debounceMs = SEARCH_DEBOUNCE_MS, limit = 5 }: UseSearchSuggestionsOptions = {}
): UseSearchSuggestionsResult {
  const debouncedQuery = useDebouncedValue(query, debounceMs);
  // The Rust search SSOT's gate (trimmed, ≥2 chars) — the same rule the server
  // enforces, so we never fire a query it would answer emptily.
  const shouldQuery = autocompleteQueryIsSearchable(debouncedQuery);
  // The gate applied to the LIVE query: what the user has typed, not what has
  // settled. It is what separates "idle" from "loading" during the debounce
  // window, so the panel says "searching…" the moment the 2nd character lands.
  const liveIsSearchable = autocompleteQueryIsSearchable(query);

  const { data, isFetching, error } = useQuery({
    queryKey: shouldQuery ? queryKeys.search.suggestions(debouncedQuery, limit) : [],
    queryFn: async () => {
      const result = await bff('/api/search/suggestions', {
        query: debouncedQuery,
        limit,
      });
      if (!result.success) {
        throw bffError(result.error, 'Search failed');
      }
      return result.data!.sections;
    },
    enabled: shouldQuery,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const sections = shouldQuery ? (data ?? []) : [];
  const errorMessage = error instanceof Error ? error.message : null;
  // In flight OR still debouncing. The second half matters: without it the
  // gap between the keystroke and the request read as "completed with zero
  // matches", which is the exact lie this whole union exists to prevent.
  const isLoading = (liveIsSearchable && query !== debouncedQuery) || (shouldQuery && isFetching);

  return {
    state: deriveSuggestionViewState({
      queryIsSearchable: liveIsSearchable,
      settledQuery: debouncedQuery.trim(),
      isLoading,
      error: errorMessage,
      results: sections,
      isEmpty: noSuggestions,
    }),
    sections,
    isLoading,
    error: errorMessage,
  };
}
